import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import MaterialIcon from '../components/MaterialIcon';
import CommandAnalyticsStrip from '../components/analytics/CommandAnalyticsStrip';
import api from '../api';
import {
  formatDate,
  formatDaysLabel,
  formatNumber,
  formatRelativeTime,
} from '../utils/formatters';

const PAGE_SIZE = 4;

function buildPortalSummary(records = []) {
  const counts = new Map();
  for (const record of records) {
    for (const portal of record.connectedPortals || []) {
      const label = portal.label || 'Suite Access';
      counts.set(label, (counts.get(label) || 0) + 1);
    }
  }

  return [...counts.entries()]
    .sort((left, right) => right[1] - left[1])
    .slice(0, 3)
    .map(([label, value], index) => ({
      label,
      value,
      width: index === 0 ? 72 : index === 1 ? 40 : 12,
      opacity: index === 0 ? '' : index === 1 ? 'opacity-60' : 'opacity-30',
    }));
}

function buildWeeklyRenewalCounts(records = []) {
  const buckets = [
    { label: 'W1', count: 0 },
    { label: 'W2', count: 0 },
    { label: 'W3', count: 0 },
    { label: 'W4', count: 0 },
  ];

  for (const record of records) {
    const days = record.daysToNextRenewal;
    if (days == null || days < 0) continue;
    if (days <= 7) buckets[0].count += 1;
    else if (days <= 14) buckets[1].count += 1;
    else if (days <= 21) buckets[2].count += 1;
    else if (days <= 28) buckets[3].count += 1;
  }

  const max = Math.max(...buckets.map((bucket) => bucket.count), 1);
  return buckets.map((bucket, index) => ({
    ...bucket,
    height: `${Math.max(48, (bucket.count / max) * 128)}px`,
    critical: index === 2 && bucket.count === max && max > 0,
  }));
}

function riskLabel(record) {
  if (record.criticalIssueCount > 0) return { label: 'HIGH', width: '85%', tone: 'text-[#fd6c00]', bar: 'bg-[#fd6c00]' };
  if (record.warningIssueCount > 0 || record.expiringServiceCount > 0) return { label: 'MODERATE', width: '45%', tone: 'text-[#fd6c00]', bar: 'bg-[#fd6c00]' };
  return { label: 'LOW', width: '20%', tone: 'text-[#f1c100] opacity-60', bar: 'bg-[#f1c100]' };
}

export default function Organizations() {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [metrics, setMetrics] = useState({
    total: 0,
    suiteReady: 0,
    needsAttention: 0,
    connectedPortals: 0,
    criticalAccounts: 0,
  });
  const [commandSummary, setCommandSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshingCommand, setRefreshingCommand] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);

  const loadRecords = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/admin/platform/business-profiles');
      setRecords(data.records || []);
      setMetrics(data.metrics || {});
      setCommandSummary(
        data.commandAnalytics
          ? {
              ...data.commandAnalytics,
              sources: data.sources || [],
              freshness: data.freshness || null,
              externalSummary: data.externalSummary || null,
            }
          : null,
      );
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not load business management records');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const refreshCommandAnalytics = async () => {
    setRefreshingCommand(true);
    try {
      const { data } = await api.post('/admin/platform/command-analytics/refresh', {
        range: '24h',
      });
      setCommandSummary(data);
      await loadRecords();
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not refresh command analytics');
    } finally {
      setRefreshingCommand(false);
    }
  };

  const model = useMemo(() => {
    const portalSummary = buildPortalSummary(records);
    const riskScore = metrics.total
      ? Math.min(
          100,
          Math.round(
            ((Number(metrics.criticalAccounts || 0) * 3 + Number(metrics.needsAttention || 0)) /
              Math.max(Number(metrics.total || 0), 1)) *
              32
          )
        )
      : 0;
    const weeklyRenewals = buildWeeklyRenewalCounts(records);
    const sortedRecords = [...records].sort((left, right) => {
      const leftRisk = (left.criticalIssueCount || 0) * 100 + (left.warningIssueCount || 0) * 10;
      const rightRisk = (right.criticalIssueCount || 0) * 100 + (right.warningIssueCount || 0) * 10;
      return rightRisk - leftRisk;
    });
    const maxPage = Math.max(1, Math.ceil(sortedRecords.length / PAGE_SIZE));
    const currentPage = Math.min(page, maxPage);
    const start = (currentPage - 1) * PAGE_SIZE;

    return {
      portalSummary,
      riskScore,
      weeklyRenewals,
      sortedRecords,
      currentPage,
      maxPage,
      pageRecords: sortedRecords.slice(start, start + PAGE_SIZE),
      toastRecords: sortedRecords.slice(0, 2),
    };
  }, [metrics, page, records]);

  useEffect(() => {
    if (page > model.maxPage) {
      setPage(model.maxPage);
    }
  }, [model.maxPage, page]);

  return (
    <Layout searchPlaceholder="Search profiles...">
      <div className="space-y-8">
        {error ? (
          <div className="rounded-lg border border-[#93000a] bg-[#2a1112] px-4 py-3 text-sm text-[#ffdad6]">
            {error}
          </div>
        ) : null}

        {commandSummary ? (
          <CommandAnalyticsStrip
            summary={commandSummary}
            title="Business telemetry"
            description="Business Manager now mixes Shimbirr profile readiness with the real product-origin signals coming from PMS, Taban Food, and Taban Website."
            onRefresh={refreshCommandAnalytics}
            refreshing={refreshingCommand}
            compact
          />
        ) : null}

        <section className="space-y-8">
          <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.24em] text-[#f1c100]">
                <span className="status-pulse h-2 w-2 rounded-full bg-[#f1c100]" />
                Live Operations Monitor
              </div>
              <h1 className="font-['Geist'] text-[clamp(2rem,5vw,3rem)] font-black tracking-[-0.03em] text-white">
                COMMAND CENTER
              </h1>
              <p className="max-w-2xl text-[#d2c5ab]">
                Real-time business profile monitoring, risk assessment, and suite accessibility across all active Taban portals.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => navigate(records[0] ? `/organizations/${records[0].userId}` : '/organizations')}
                className="glow-yellow flex h-12 items-center gap-2 rounded-lg bg-[#ffcc00] px-6 font-bold text-[#3d2f00] transition hover:brightness-110"
              >
                <MaterialIcon name="add" className="text-xl" />
                PROFILE DESK
              </button>
              <button
                type="button"
                onClick={loadRecords}
                className="flex h-12 items-center gap-2 rounded-lg border border-[#9a9078] px-6 font-bold text-white transition hover:bg-[#201f1f]"
              >
                <MaterialIcon name="download" className="text-xl" />
                EXPORT DATA
              </button>
            </div>
          </div>

          {loading ? (
            <div className="rounded-lg border border-[#4e4632] bg-[#1c1b1b] p-6 text-sm text-[#d2c5ab]">
              Loading live business profiles...
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                <div className="rounded-lg border border-[#4e4632] bg-[#1c1b1b] p-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm uppercase tracking-[0.18em] text-[#d2c5ab]">
                      Suite Access
                    </h3>
                    <MaterialIcon name="key" className="text-[#f1c100]" />
                  </div>
                  <div className="mt-6 flex flex-col gap-4">
                    {model.portalSummary.length ? (
                      model.portalSummary.map((item) => (
                        <div key={item.label} className="flex flex-col gap-1">
                          <div className="flex justify-between text-sm text-white">
                            <span>{item.label}</span>
                            <span>{formatNumber(item.value)}</span>
                          </div>
                          <div className="h-2 w-full overflow-hidden rounded-full bg-[#353534]">
                            <div
                              className={`h-full bg-[#f1c100] ${item.opacity}`}
                              style={{ width: `${item.width}%` }}
                            />
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-[#d2c5ab]">No connected portal suite data has synced yet.</p>
                    )}
                  </div>
                </div>

                <div className="rounded-lg border border-[#4e4632] bg-[#1c1b1b] p-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm uppercase tracking-[0.18em] text-[#d2c5ab]">
                      Global Risk Factor
                    </h3>
                    <MaterialIcon name="warning" className="text-[#fd6c00]" />
                  </div>
                  <div className="mt-6 flex flex-col items-center justify-center gap-2">
                    <div className="relative flex h-32 w-32 items-center justify-center">
                      <svg className="h-full w-full -rotate-90" viewBox="0 0 140 140">
                        <circle cx="70" cy="70" r="58" fill="transparent" stroke="#353534" strokeWidth="12" />
                        <circle
                          cx="70"
                          cy="70"
                          r="58"
                          fill="transparent"
                          stroke="#fd6c00"
                          strokeWidth="12"
                          strokeDasharray="364.4"
                          strokeDashoffset={364.4 - (model.riskScore / 100) * 364.4}
                        />
                      </svg>
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <span className="font-['Geist'] text-3xl font-black text-white">
                          {model.riskScore}%
                        </span>
                        <span className="text-[10px] text-[#d2c5ab]">NOMINAL</span>
                      </div>
                    </div>
                    <p className="text-center text-sm text-[#d2c5ab]">
                      Risk detected in <span className="font-bold text-[#fd6c00]">{formatNumber(metrics.needsAttention || 0)} portals</span>. Immediate review required for legacy nodes.
                    </p>
                  </div>
                </div>

                <div className="rounded-lg border border-[#4e4632] bg-[#1c1b1b] p-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm uppercase tracking-[0.18em] text-[#d2c5ab]">
                      Renewal Pressure
                    </h3>
                    <MaterialIcon name="event_repeat" className="text-[#f1c100]" />
                  </div>
                  <div className="mt-6 flex min-h-[12rem] items-end justify-between gap-2 pt-4">
                    {model.weeklyRenewals.map((bucket) => (
                      <div key={bucket.label} className="flex w-full flex-col items-center gap-2">
                        <div
                          className={`relative w-full rounded-t-sm ${bucket.critical ? 'bg-[#fd6c00]' : 'bg-[#f1c100]'} ${bucket.critical ? '' : bucket.label === 'W1' ? 'opacity-20' : bucket.label === 'W2' ? 'opacity-40' : 'opacity-60'}`}
                          style={{ height: bucket.height }}
                        >
                          {bucket.critical ? (
                            <div className="absolute -top-6 left-1/2 -translate-x-1/2 text-[10px] font-black text-[#fd6c00]">
                              CRITICAL
                            </div>
                          ) : null}
                        </div>
                        <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-[#d2c5ab]">
                          {bucket.label}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex flex-col gap-4 border-b border-[#4e4632] pb-4 md:flex-row md:items-center md:justify-between">
                  <h2 className="font-['Geist'] text-2xl font-bold text-white">
                    LIVE BUSINESS PROFILES
                  </h2>
                  <div className="flex flex-wrap items-center gap-4">
                    <div className="flex items-center gap-2 text-sm text-[#d2c5ab]">
                      <MaterialIcon name="filter_list" className="text-sm" />
                      FILTER BY RISK:
                      <span className="rounded bg-[#2a2a2a] px-2 py-1 text-xs text-white">ALL LEVELS</span>
                    </div>
                    <div className="text-sm text-[#d2c5ab]">
                      {formatNumber(metrics.total || 0)} RECORDS TOTAL
                    </div>
                  </div>
                </div>

                {commandSummary?.entities?.length ? (
                  <div className="rounded-lg border border-[#4e4632] bg-[#1c1b1b] p-6">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <h3 className="font-['Geist'] text-2xl font-bold text-white">
                          Connected Product Signals
                        </h3>
                        <p className="mt-2 text-sm text-[#d2c5ab]">
                          These entities come from the connected products feeding Shimbirr Command, so admins can see which portal or workspace is producing the current load.
                        </p>
                      </div>
                      <span className="rounded-full border border-[#4e4632] bg-[#111111] px-3 py-1 text-xs font-bold uppercase tracking-[0.16em] text-[#f7d88a]">
                        {commandSummary.lastUpdatedAt ? formatRelativeTime(commandSummary.lastUpdatedAt) : 'live'}
                      </span>
                    </div>

                    <div className="mt-5 grid gap-3 xl:grid-cols-2">
                      {commandSummary.entities.slice(0, 6).map((entity) => (
                        <div
                          key={`${entity.productKey}-${entity.key}`}
                          className="rounded-[1.3rem] border border-[#4e4632] bg-[#111111] p-4"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <p className="text-sm font-bold text-white">{entity.label}</p>
                              <p className="mt-1 text-xs uppercase tracking-[0.16em] text-[#9a9078]">
                                {entity.productLabel}
                              </p>
                            </div>
                            <span className="rounded-full bg-[#ffcc00] px-3 py-1 text-xs font-bold text-[#3d2f00]">
                              {formatNumber(entity.value || 0)}
                            </span>
                          </div>
                          {entity.meta ? (
                            <p className="mt-3 text-sm text-[#d2c5ab]">{entity.meta}</p>
                          ) : null}
                          {entity.actionUrl ? (
                            <div className="mt-4">
                              <a
                                href={entity.actionUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-2 rounded-full border border-[#f1c100]/35 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-[#f7d88a] transition hover:bg-[#f1c100] hover:text-[#241a00]"
                              >
                                <MaterialIcon name="open_in_new" className="text-xs" />
                                Open source
                              </a>
                            </div>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}

                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b border-[#353534] text-left text-xs uppercase tracking-[0.18em] text-[#d2c5ab]">
                        <th className="px-4 py-4 font-medium">Profile Name</th>
                        <th className="px-4 py-4 font-medium">Portal Suite</th>
                        <th className="px-4 py-4 font-medium">Renewal Date</th>
                        <th className="px-4 py-4 font-medium">Support Risk</th>
                        <th className="px-4 py-4 text-right font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#353534] text-white">
                      {model.pageRecords.length ? (
                        model.pageRecords.map((record) => {
                          const risk = riskLabel(record);
                          const initials = (record.businessName || record.displayName)
                            .split(' ')
                            .slice(0, 2)
                            .map((part) => part.charAt(0).toUpperCase())
                            .join('')
                            .slice(0, 2);

                          return (
                            <tr
                              key={record.userId}
                              className="cursor-pointer transition-colors hover:bg-[#201f1f]"
                              onClick={() => navigate(`/organizations/${record.userId}`)}
                            >
                              <td className="px-4 py-4">
                                <div className="flex items-center gap-3">
                                  <div className={`flex h-8 w-8 items-center justify-center rounded border border-[#4e4632] bg-[#2a2a2a] font-bold ${record.criticalIssueCount ? 'text-[#fd6c00]' : 'text-[#f1c100]'}`}>
                                    {initials || 'BP'}
                                  </div>
                                  <div className="flex flex-col">
                                    <span className="font-bold">{record.businessName || record.displayName}</span>
                                    <span className="text-[10px] text-[#d2c5ab]">
                                      ID: {record.shimbirrId || record.userId.slice(-8).toUpperCase()}
                                    </span>
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-4">
                                <span className="rounded border border-[#4e4632] bg-[#353534] px-2 py-1 text-xs text-[#e5e2e1]">
                                  {record.connectedPortals?.[0]?.label || 'Core Suite'}
                                </span>
                              </td>
                              <td className={`px-4 py-4 ${record.daysToNextRenewal != null && record.daysToNextRenewal <= 2 ? 'font-bold text-[#fd6c00]' : 'text-[#d2c5ab]'}`}>
                                {record.nextRenewalAt ? (
                                  record.daysToNextRenewal != null && record.daysToNextRenewal <= 2
                                    ? formatDaysLabel(record.daysToNextRenewal)
                                    : formatDate(record.nextRenewalAt)
                                ) : (
                                  'No renewal date'
                                )}
                              </td>
                              <td className="px-4 py-4">
                                <div className="flex items-center gap-2">
                                  <div className="h-1 w-full max-w-[80px] overflow-hidden rounded-full bg-[#353534]">
                                    <div className={`h-full ${risk.bar}`} style={{ width: risk.width }} />
                                  </div>
                                  <span className={`text-xs font-bold ${risk.tone}`}>
                                    {risk.label}
                                  </span>
                                </div>
                              </td>
                              <td className="px-4 py-4 text-right">
                                <span className={`inline-flex items-center gap-1.5 rounded border px-2 py-1 text-[10px] font-bold ${
                                  record.criticalIssueCount
                                    ? 'border-[#fd6c00] text-[#fd6c00]'
                                    : record.daysToNextRenewal != null && record.daysToNextRenewal <= 2
                                      ? 'border-[#fd6c00] text-[#fd6c00]'
                                      : 'border-[#f1c100] text-[#f1c100]'
                                }`}>
                                  <span className={`h-1.5 w-1.5 rounded-full ${
                                    record.criticalIssueCount || (record.daysToNextRenewal != null && record.daysToNextRenewal <= 2)
                                      ? 'bg-[#fd6c00]'
                                      : 'bg-[#f1c100]'
                                  }`} />
                                  {record.criticalIssueCount
                                    ? 'ATTENTION'
                                    : record.daysToNextRenewal != null && record.daysToNextRenewal <= 2
                                      ? 'RENEWAL DUE'
                                      : 'ACTIVE'}
                                </span>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="5" className="px-4 py-8 text-center text-sm text-[#d2c5ab]">
                            No business profiles are available yet.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="mt-4 flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() => setPage((current) => Math.max(1, current - 1))}
                    disabled={model.currentPage === 1}
                    className="flex items-center gap-1 text-sm uppercase tracking-[0.12em] text-[#d2c5ab] transition hover:text-white disabled:opacity-40"
                  >
                    <MaterialIcon name="chevron_left" className="text-sm" /> Previous
                  </button>

                  <div className="flex gap-2">
                    {Array.from({ length: model.maxPage }, (_, index) => index + 1).slice(0, 3).map((pageNumber) => (
                      <button
                        key={pageNumber}
                        type="button"
                        onClick={() => setPage(pageNumber)}
                        className={`flex h-8 w-8 items-center justify-center rounded text-xs font-bold ${
                          model.currentPage === pageNumber
                            ? 'bg-[#ffcc00] text-[#3d2f00]'
                            : 'text-[#d2c5ab] hover:text-white'
                        }`}
                      >
                        {pageNumber}
                      </button>
                    ))}
                  </div>

                  <button
                    type="button"
                    onClick={() => setPage((current) => Math.min(model.maxPage, current + 1))}
                    disabled={model.currentPage === model.maxPage}
                    className="flex items-center gap-1 text-sm uppercase tracking-[0.12em] text-[#d2c5ab] transition hover:text-white disabled:opacity-40"
                  >
                    Next <MaterialIcon name="chevron_right" className="text-sm" />
                  </button>
                </div>
              </div>
            </>
          )}
        </section>

        {!loading && model.toastRecords.length ? (
          <aside className="fixed bottom-6 right-6 z-40 hidden w-80 flex-col gap-3 xl:flex">
            {model.toastRecords.map((record, index) => (
              <div
                key={record.userId}
                className={`rounded border-l-4 bg-[#201f1f] p-4 shadow-2xl ${
                  index === 0 ? 'border-[#fd6c00]' : 'border-[#f1c100]'
                }`}
              >
                <div className="mb-2 flex items-start justify-between">
                  <span className={`text-[10px] font-black uppercase ${
                    index === 0 ? 'text-[#fd6c00]' : 'text-[#f1c100]'
                  }`}>
                    {index === 0 ? 'Urgent Alert' : 'System Notification'}
                  </span>
                  <MaterialIcon name="close" className="cursor-pointer text-sm text-[#d2c5ab]" />
                </div>
                <p className="text-xs leading-relaxed text-white">
                  {index === 0 ? (
                    <>
                      Renewal pressure detected for <strong>{record.businessName || record.displayName}</strong>. Contract cadence is <span className="font-bold text-[#fd6c00]">{formatDaysLabel(record.daysToNextRenewal)}</span>. System auto-escalation initiated.
                    </>
                  ) : (
                    <>
                      Weekly analytics report for <strong>{record.businessName || record.displayName}</strong> synced {formatRelativeTime(record.sync?.updatedAt)}.
                    </>
                  )}
                </p>
              </div>
            ))}
          </aside>
        ) : null}
      </div>
    </Layout>
  );
}
